import {API_BASE} from '../apiConfig';

/**
 * Сохранение итогов локальной партии (POST /api/users/me/local-matches) —
 * так, чтобы они не потерялись.
 *
 * Запись локальной партии заводится на ее старте незавершенной, и закрывают
 * ее только итоги. Один потерянный запрос — и доигранная партия навсегда
 * оставалась бы в истории «не завершённой», а счетчики профиля и задание за
 * бой с ботами так и не сдвинулись бы. Поэтому:
 *  - запрос повторяется при сбоях сети и сервера (до восьми попыток за ~9
 *    минут) и уходит с keepalive — запрос, который уже в полете, дойдет и
 *    после закрытия вкладки;
 *  - итоги на время сохранения лежат в localStorage: если страница закрылась
 *    или перезагрузилась (в том числе сама — на новую версию после выкладки),
 *    пока шли повторы, они досылаются при следующем входе (см.
 *    resendPendingLocalMatchResults).
 *
 * Повтор безопасен только с идентификатором партии в теле (runId): по нему
 * сервер узнает уже сохраненный матч и второй раз ни матч не заводит, ни
 * счетчики не крутит (отвечает isNewMatch: false).
 */

// Паузы перед повторными попытками. Сбой в момент конца партии обычно
// короткий (моргнула сеть, сервер перезапускается при выкладке) — первые
// повторы частые; дальше паузы растут
export const LOCAL_MATCH_SAVE_RETRY_DELAYS_MS = [1000, 3000, 10000, 30000, 60000, 120000, 300000];

// Сколько ждать ответа на одну попытку. Запрос, зависший без ответа (мертвая
// точка сети), иначе держал бы все расписание до таймаута самой системы
export const LOCAL_MATCH_SAVE_ATTEMPT_TIMEOUT_MS = 30000;

export const PENDING_LOCAL_MATCH_SAVES_STORAGE_KEY = 'strategikon:pendingLocalMatchResults';
// Итоги старше этого срока не досылаем: партия давняя, а запись могла
// остаться от другой версии клиента
const PENDING_SAVE_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;
const PENDING_SAVES_MAX_COUNT = 20;

// Ответы, после которых повтор имеет смысл: сервер не смог ответить по делу
// (5xx), не дождался запроса (408) или попросил подождать (429)
const checkIfRetryableStatus = (status) => status >= 500 || status === 408 || status === 429;

// Нет сессии: повтор сейчас даст то же, но после входа итоги еще можно
// дослать — из очереди на досылку они не убираются
const UNAUTHORIZED_STATUS = 401;

// === Очередь на досылку (localStorage) ===

const getPendingSaveKey = (userId, runId) => `${userId}:${runId}`;

const readPendingSaves = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(PENDING_LOCAL_MATCH_SAVES_STORAGE_KEY) || '[]');
    if (!Array.isArray(stored)) {
      return [];
    }
    const oldestAllowed = Date.now() - PENDING_SAVE_MAX_AGE_MS;
    return stored.filter((entry) => (
      entry && typeof entry.userId === 'string' && typeof entry.runId === 'string'
      && entry.body && typeof entry.body === 'object' && entry.savedAt >= oldestAllowed
    ));
  } catch (error) {
    // Хранилище недоступно (приватный режим, запрет) или в нем мусор —
    // работаем без досылки
    return [];
  }
};

const writePendingSaves = (entries) => {
  try {
    if (entries.length === 0) {
      localStorage.removeItem(PENDING_LOCAL_MATCH_SAVES_STORAGE_KEY);
    } else {
      localStorage.setItem(PENDING_LOCAL_MATCH_SAVES_STORAGE_KEY, JSON.stringify(entries));
    }
  } catch (error) {
    // Нет места или хранилище недоступно: сохранение пойдет и без досылки
  }
};

const rememberPendingSave = (userId, body) => {
  const storedEntries = readPendingSaves();
  const isSameSave = (entry) => entry.userId === userId && entry.runId === body.runId;
  // Досылка тех же итогов срок хранения не продлевает: он считается от
  // первой попытки
  const savedAt = storedEntries.find(isSameSave)?.savedAt ?? Date.now();
  const entries = storedEntries.filter((entry) => !isSameSave(entry));
  entries.push({userId, runId: body.runId, body, savedAt});
  writePendingSaves(entries.slice(-PENDING_SAVES_MAX_COUNT));
};

const forgetPendingSave = (userId, runId) => {
  writePendingSaves(readPendingSaves().filter((entry) => !(entry.userId === userId && entry.runId === runId)));
};

// Сохранения, которые идут прямо сейчас в этой вкладке: досылка не должна
// запускать второй цикл повторов для тех же итогов
const savesInFlight = new Set();

// === Ожидания ===

/**
 * Пауза перед повтором. Кончается раньше срока, если браузер сообщил, что сеть
 * вернулась: ждать остаток длинной паузы тогда незачем
 */
export const waitForLocalMatchSaveRetry = (delayMs) => new Promise((resolve) => {
  const hasWindow = typeof window !== 'undefined' && typeof window.addEventListener === 'function';
  let timeoutId = null;
  const finish = () => {
    clearTimeout(timeoutId);
    if (hasWindow) {
      window.removeEventListener('online', finish);
    }
    resolve();
  };
  timeoutId = setTimeout(finish, delayMs);
  if (hasWindow) {
    window.addEventListener('online', finish);
  }
});

// Сколько ждать возврата сети перед попыткой, если браузер говорит, что ее
// нет. Дольше не ждем: браузер может и ошибаться (сеть есть, а он считает
// себя офлайн) — тогда запрос должен уйти все равно
export const LOCAL_MATCH_SAVE_OFFLINE_WAIT_MS = 60000;

/**
 * Дождаться сети, если браузер говорит, что ее нет: запрос все равно упал бы
 * сразу и только сжег бы попытку. Локальная партия играется и без сети —
 * телефон мог уйти в офлайн надолго. Ожидание ограничено: по истечении срока
 * попытка делается в любом случае.
 *
 * @returns {Promise|null} null — сеть есть, ждать нечего: запрос должен уйти
 *   сразу, в том же такте (страницу могут закрыть в любой момент)
 */
export const waitForLocalMatchSaveNetwork = (maxWaitMs = LOCAL_MATCH_SAVE_OFFLINE_WAIT_MS) => {
  const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (!isOffline || typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
    return null;
  }
  return waitForLocalMatchSaveRetry(maxWaitMs);
};

// === Отправка ===

/**
 * Одна попытка. Исходы: {data} — сохранено; {isRejected} — сервер отказал
 * самому запросу, повторять незачем; {isUnauthorized} — нет сессии;
 * {failure} — сбой, после которого нужен повтор
 */
const sendLocalMatchResults = async (requestBody, {fetchImpl, attemptTimeoutMs}) => {
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timeoutId = controller ? setTimeout(() => controller.abort(), attemptTimeoutMs) : null;
  try {
    const response = await fetchImpl(`${API_BASE}/users/me/local-matches`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      credentials: 'include',
      // Итоги показываются в самом конце партии — вкладку нередко закрывают
      // сразу: запрос, который уже ушел, должен дойти и после выгрузки страницы
      keepalive: true,
      signal: controller ? controller.signal : undefined,
      body: requestBody,
    });
    if (response.ok) {
      return {data: await response.json()};
    }
    if (response.status === UNAUTHORIZED_STATUS) {
      return {isUnauthorized: true};
    }
    if (!checkIfRetryableStatus(response.status)) {
      return {isRejected: true, status: response.status};
    }
    return {failure: `status ${response.status}`};
  } catch (error) {
    // Запрос не дошел (нет сети, сервер недоступен), оборван по таймауту
    // попытки или ответ не разобрать
    return {failure: error};
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * @param {Object} body - тело запроса; обязательно с runId (см. выше)
 * @param {Object} [options]
 * @param {string|null} [options.userId] - чьи итоги: с ним они на время
 *   сохранения кладутся в очередь на досылку. Без него досылки нет
 * @param {Function} [options.fetchImpl] - подмена fetch (тесты)
 * @param {number[]} [options.retryDelaysMs] - паузы перед повторами
 * @param {number} [options.attemptTimeoutMs] - срок одной попытки
 * @param {Function} [options.wait] - (delayMs) => Promise, пауза перед повтором
 * @param {Function} [options.waitForNetwork] - () => Promise|null, ожидание
 *   сети; null — ждать нечего
 * @returns {Promise<{data: Object|null, attemptsCount: number}>} data — ответ
 *   сервера или null, если сохранить не удалось; attemptsCount — сколько
 *   запросов ушло. Промис не отклоняется
 */
export const saveLocalMatchResults = async (body, {
  userId = null,
  // Не ссылка на fetch, а вызов по имени: подмена global.fetch в тестах и
  // полифилы должны подхватываться в момент запроса
  fetchImpl = (...args) => fetch(...args),
  retryDelaysMs = LOCAL_MATCH_SAVE_RETRY_DELAYS_MS,
  attemptTimeoutMs = LOCAL_MATCH_SAVE_ATTEMPT_TIMEOUT_MS,
  wait = waitForLocalMatchSaveRetry,
  waitForNetwork = waitForLocalMatchSaveNetwork,
} = {}) => {
  const pendingKey = userId && body?.runId ? getPendingSaveKey(userId, body.runId) : null;
  if (pendingKey) {
    savesInFlight.add(pendingKey);
    rememberPendingSave(userId, body);
  }
  // Тело одно на все попытки: повтор должен нести тот же runId и те же итоги
  const requestBody = JSON.stringify(body);

  try {
    for (let attempt = 0; ; attempt += 1) {
      const networkWait = waitForNetwork();
      if (networkWait) {
        await networkWait;
      }
      const outcome = await sendLocalMatchResults(requestBody, {fetchImpl, attemptTimeoutMs});
      const attemptsCount = attempt + 1;

      if (outcome.data) {
        if (pendingKey) {
          forgetPendingSave(userId, body.runId);
        }
        return {data: outcome.data, attemptsCount};
      }
      if (outcome.isRejected) {
        console.error('Failed to save local match: server rejected the request', outcome.status);
        // Тело сервер не принял — досылка даст тот же отказ
        if (pendingKey) {
          forgetPendingSave(userId, body.runId);
        }
        return {data: null, attemptsCount};
      }
      if (outcome.isUnauthorized) {
        // Итоги остаются в очереди: дошлем после входа
        console.error('Failed to save local match: not authenticated');
        return {data: null, attemptsCount};
      }
      if (attempt >= retryDelaysMs.length) {
        // Итоги остаются в очереди: дошлем при следующем запуске
        console.error('Failed to save local match: retries exhausted', outcome.failure);
        return {data: null, attemptsCount};
      }
      await wait(retryDelaysMs[attempt]);
    }
  } finally {
    if (pendingKey) {
      savesInFlight.delete(pendingKey);
    }
  }
};

/**
 * Дослать итоги локальных партий этого игрока, не дошедшие в прошлый раз:
 * страница закрылась или перезагрузилась, пока шли повторы, либо попытки
 * кончились. Вызывается после входа. Итоги досылаются по очереди — две
 * партии одним залпом не должны спорить за одно и то же задание.
 *
 * @param {string} userId
 * @param {Object} [options] - те же, что у saveLocalMatchResults, и
 *   onSaved(data) — ответ сервера на каждые дошедшие итоги
 */
export const resendPendingLocalMatchResults = async (userId, {onSaved, ...saveOptions} = {}) => {
  if (!userId) {
    return;
  }
  const pendingEntries = readPendingSaves().filter((entry) => (
    entry.userId === userId && !savesInFlight.has(getPendingSaveKey(entry.userId, entry.runId))
  ));
  for (const entry of pendingEntries) {
    const {data} = await saveLocalMatchResults(entry.body, {...saveOptions, userId});
    if (data && onSaved) {
      onSaved(data);
    }
  }
};
